import { Command } from 'commander'
import { setDevMode, setApiUrl } from './lib/config'

// Version injected by esbuild at build time
declare const __CLI_VERSION__: string

// Import commands
import { initCommand } from './commands/init'

import { registerCommand } from './commands/register'
import { statusCommand } from './commands/status'

import { taskCommand } from './commands/task'
import { promptCommand } from './commands/prompt'
import { orgListCommand, orgSwitchCommand, orgRefreshCommand } from './commands/org'
import { contextCommand } from './commands/context'
import { syncCommand } from './commands/sync'

import { projectCommand } from './commands/project'
import { previewCommand } from './commands/preview'

const program = new Command()

program
  .name('flightdesk')
  .description('FlightDesk CLI - AI task management for Claude Code sessions')
  .version(__CLI_VERSION__)
  .option('--dev', 'Use local development API (localhost:3000)')
  .option('--api <url>', 'Use custom API URL')

// Process --dev and --api flags before command execution
program.hook('preAction', () => {
  const opts = program.opts()
  if (opts.api) {
    setApiUrl(opts.api)
    console.log(`🔧 Using custom API: ${opts.api}\n`)
  } else if (opts.dev) {
    setDevMode(true)
    console.log('🔧 Development mode: using http://localhost:3000\n')
  }
})

// Initialize configuration
program
  .command('init')
  .description('Configure FlightDesk CLI with your API credentials')
  .action(initCommand)

// Register a session with a task
program
  .command('register [task-id]')
  .description('Register a Claude Code session with a FlightDesk task (auto-detects project from git repo)')
  .option('-p, --project <id>', 'Project ID (auto-detected from git repo if not provided)')
  .option('--subproject <id>', 'Subproject ID (required if project has subprojects configured)')
  .option('--view-url <url>', 'Claude Code session view URL')
  .option('--teleport-id <id>', 'Claude Code teleport ID')
  .option('--title <title>', 'Task title (creates new task if task-id not provided)')
  .option('--description <description>', 'Task description')
  .option('--prompt <prompt>', 'Prompt to pass to claude --remote (avoids pipe detection issues)')
  .action(registerCommand)

// Project management
const project = program
  .command('project')
  .description('Project management commands')

project
  .command('list')
  .description('List projects in the active organization')
  .action(() => projectCommand('list', {}))

// Task management
const task = program
  .command('task')
  .description('Task management commands')

task
  .command('create')
  .description('Create a new task')
  .requiredOption('-p, --project <id>', 'Project ID')
  .requiredOption('-t, --title <title>', 'Task title')
  .option('-d, --description <description>', 'Task description')
  .option('--subproject <id>', 'Subproject ID (required if project has subprojects configured)')
  .action((options) => taskCommand('create', options))

task
  .command('list')
  .description('List tasks')
  .option('-p, --project <id>', 'Filter by project ID')
  .option('--status <status>', 'Filter by status')
  .action((options) => taskCommand('list', options))

task
  .command('status <task-id>')
  .description('Get task status')
  .action((taskId) => taskCommand('status', { taskId }))

task
  .command('update <task-id>')
  .description('Update task')
  .option('-s, --status <status>', 'New status')
  .option('--branch <branch>', 'Branch name')
  .option('--pr-url <url>', 'Pull request URL')
  .option('--pr-number <number>', 'Pull request number (use if --pr-url is not enough)')
  .option('--session <session>', 'Claude Code session (URL or session ID)')
  .action((taskId, options) => taskCommand('update', { taskId, ...options }))

task
  .command('sync <task-id>')
  .description('Sync PR review state (Copilot feedback, Claude reviews, human reviews) from GitHub')
  .action((taskId) => taskCommand('sync', { taskId }))

// Status overview
program
  .command('status')
  .description('Show status of all active tasks')
  .option('-p, --project <id>', 'Filter by project')
  .action(statusCommand)

// Get prompt for a task
program
  .command('prompt <task-id>')
  .description('Get a prompt for a task (ready to paste into Claude)')
  .option('--type <type>', 'Prompt type: review, test_plan, summary, handoff', 'review')
  .action(promptCommand)

// Organization management
const org = program
  .command('org')
  .description('Organization management')

org
  .command('list')
  .description('List your organizations')
  .action(orgListCommand)

org
  .command('switch <name>')
  .description('Switch active organization')
  .action(orgSwitchCommand)

org
  .command('refresh')
  .description('Refresh organizations from API')
  .action(orgRefreshCommand)

// Context detection
program
  .command('context')
  .description('Show current repository context and mapped organization')
  .action(contextCommand)

// Sync repo mappings
program
  .command('sync')
  .description('Refresh project-to-repository mappings from all organizations')
  .action(syncCommand)

// Preview environment commands
const preview = program
  .command('preview')
  .description('Preview environment management')

preview
  .command('status <task-id>')
  .description('Show preview environment status')
  .action((taskId) => previewCommand('status', { taskId }))

preview
  .command('logs <task-id>')
  .description('Get logs from preview environment')
  .option('-n, --lines <lines>', 'Number of log lines (1-10000)', '100')
  .option('-f, --follow', 'Follow log output')
  .action((taskId, options) => {
    const lines = Math.min(Math.max(Number.parseInt(options.lines || '100'), 1), 10000)
    previewCommand('logs', { taskId, lines, follow: options.follow })
  })

preview
  .command('mount <task-id>')
  .description('Mount preview environment filesystem via SSHFS')
  .option('-d, --directory <path>', 'Custom mount directory')
  .action((taskId, options) => previewCommand('mount', { taskId, directory: options.directory }))

preview
  .command('unmount <task-id>')
  .description('Unmount preview environment filesystem')
  .action((taskId) => previewCommand('unmount', { taskId }))

preview
  .command('restart <task-id>')
  .description('Restart preview environment processes')
  .action((taskId) => previewCommand('restart', { taskId }))

preview
  .command('resume <task-id>')
  .description('Resume a suspended preview environment')
  .action((taskId) => previewCommand('resume', { taskId }))

preview
  .command('teardown <task-id>')
  .description('Tear down preview environment')
  .action((taskId) => previewCommand('teardown', { taskId }))

// Shorthand commands for common preview operations
program
  .command('mount <task-id>')
  .description('Mount preview environment filesystem (shorthand for "preview mount")')
  .option('-d, --directory <path>', 'Custom mount directory')
  .action((taskId, options) => previewCommand('mount', { taskId, directory: options.directory }))

program
  .command('unmount <task-id>')
  .description('Unmount preview environment filesystem (shorthand for "preview unmount")')
  .action((taskId) => previewCommand('unmount', { taskId }))

program
  .command('logs <task-id>')
  .description('Get logs from preview environment (equivalent to "preview logs")')
  .option('-n, --lines <lines>', 'Number of log lines (1-10000)', '100')
  .option('-f, --follow', 'Follow log output')
  .action((taskId, options) => {
    const lines = Math.min(Math.max(Number.parseInt(options.lines || '100'), 1), 10000)
    previewCommand('logs', { taskId, lines, follow: options.follow })
  })

program.parse()
